import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  Calendar,
  Cog,
  Globe,
  LockKeyhole,
  Palette,
  Shield,
  Timer,
  Wallet,
  X,
} from "lucide-react";

import { AuthContext } from "../../Moduels/Context/AuthContext";
import { formatRoleLabel } from "../../rbac/access";
import { useSetupProgress } from "../../Moduels/Context/SetupProgressContext";
import API from "../../services/api";
import { InputWithIcon, SelectWithIcon } from "../ui/FieldWithIcon";
import { ToastStack, useToastStack } from "../ui/ToastStack";
import SuccessCelebration from "../ui/SuccessCelebration";
import SettingsConfigurationHub from "../settings/SettingsConfigurationHub";
import ExtraWorkConfigLink from "../settings/ExtraWorkConfigLink";
import SalaryPayoutModeField, { normalizeSalaryPayoutMode } from "../../Moduels/Salary/SalaryPayoutModeField";
import "../../styles/setupProgress.css";
import "../../styles/settingsHub.css";

const SETTINGS_KEY = "app_settings:v1";

const INLINE_TABS = [
  { id: "general", label: "General", icon: Globe },
  { id: "payroll", label: "Payroll", icon: Wallet },
  { id: "preferences", label: "Preferences", icon: Palette },
  { id: "compliance", label: "Compliance", icon: Shield },
  { id: "access", label: "Security & Access", icon: LockKeyhole },
];

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveSettings(data) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(data));
}

function getRole(me, fallbackRole) {
  return (me?.role || me?.user?.role || fallbackRole || "employee").toLowerCase();
}

function normalizePayrollCycle(value) {
  const v = String(value || "monthly").toLowerCase();
  if (v === "biweekly" || v === "bi-weekly") return "biweekly";
  if (v === "weekly") return "weekly";
  return "monthly";
}

function toApiPayrollCycle(value) {
  const v = normalizePayrollCycle(value);
  return v === "biweekly" ? "biweekly" : v;
}

export default function SettingsPage() {
  const { user: me, role: authRole } = useContext(AuthContext);
  const { refetch: refetchSetupProgress } = useSetupProgress() || {};
  const { toasts, addToast, removeToast } = useToastStack();

  const role = useMemo(() => getRole(me, authRole), [me, authRole]);
  const isAdmin = role === "admin";
  const canManageRoles = role === "admin" || role === "hr";
  const canManageIam = isAdmin;
  const isStaff = isAdmin || role === "hr";

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (INLINE_TABS.some((t) => t.id === tab)) return tab;
    return null;
  });
  const [saving, setSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState("");

  const [model, setModel] = useState(() => {
    const persisted = loadSettings();
    return (
      persisted || {
        general: {
          organisation_name: me?.organisation?.name || me?.user?.organisation?.name || "",
          employee_code_prefix: "",
          timezone: "Asia/Kolkata",
          currency: "INR",
          date_format: "DD/MM/YYYY",
        },
        payroll: {
          default_structure: "Standard",
          payroll_cycle: "monthly",
          salary_payment_day: 1,
          payroll_processing_day: 25,
          monthly_salary_divisor_days: 26,
          salary_payout_mode: "with_deductions",
          tax: { tds: true, pf: true, esi: false },
        },
        preferences: {
          theme_mode: "system",
          notifications: { email: true, in_app: true },
        },
        access: {
          role,
          permissions: {
            employees: { view: true, create: role !== "employee", edit: role !== "employee", delete: role === "admin" },
            payroll: { view: role !== "employee", create: role === "admin", edit: role === "admin", delete: role === "admin" },
          },
        },
      }
    );
  });

  useEffect(() => {
    setModel((prev) => ({
      ...prev,
      access: { ...prev.access, role },
    }));
  }, [role]);

  useEffect(() => {
    if (!isStaff && !activeTab) {
      setActiveTab("preferences");
    }
  }, [isStaff, activeTab]);

  useEffect(() => {
    if (!isStaff) return;
    API.get("/setup/status")
      .then((res) => {
        const o = res.data?.org || {};
        const payrollSub = o.payroll || {};
        setModel((prev) => ({
          ...prev,
          payroll: {
            ...prev.payroll,
            payroll_cycle: normalizePayrollCycle(o.salary_cycle || payrollSub.salary_cycle || prev.payroll.payroll_cycle),
            salary_payment_day: o.salary_payment_day ?? payrollSub.salary_payment_day ?? prev.payroll.salary_payment_day ?? 1,
            payroll_processing_day:
              o.payroll_processing_day ?? payrollSub.payroll_processing_day ?? prev.payroll.payroll_processing_day ?? 25,
            monthly_salary_divisor_days:
              o.monthly_salary_divisor_days
              ?? payrollSub.monthly_salary_divisor_days
              ?? prev.payroll.monthly_salary_divisor_days
              ?? 26,
            default_structure: payrollSub.default_structure || prev.payroll.default_structure,
            salary_payout_mode: normalizeSalaryPayoutMode(
              o.salary_payout_mode || payrollSub.salary_payout_mode || prev.payroll.salary_payout_mode
            ),
            tax: payrollSub.tax || prev.payroll.tax,
          },
        }));
      })
      .catch(() => {});
  }, [isStaff]);

  useEffect(() => {
    if (!isAdmin && !canManageRoles) return;
    API.get("/organisation/me/hr-settings")
      .then((res) => {
        const hr = res.data?.hr_settings || {};
        const payrollSub = res.data?.payroll_settings || hr.payroll || {};
        const prefix = hr.employee_prefix;
        setModel((prev) => ({
          ...prev,
          general: {
            ...prev.general,
            ...(prefix ? { employee_code_prefix: String(prefix).toUpperCase() } : {}),
          },
          payroll: {
            ...prev.payroll,
            payroll_cycle: normalizePayrollCycle(hr.salary_cycle || payrollSub.salary_cycle || prev.payroll.payroll_cycle),
            salary_payment_day: hr.salary_payment_day ?? payrollSub.salary_payment_day ?? prev.payroll.salary_payment_day,
            payroll_processing_day:
              hr.payroll_processing_day ?? payrollSub.payroll_processing_day ?? prev.payroll.payroll_processing_day,
            monthly_salary_divisor_days:
              hr.monthly_salary_divisor_days
              ?? payrollSub.monthly_salary_divisor_days
              ?? prev.payroll.monthly_salary_divisor_days,
            default_structure: payrollSub.default_structure || prev.payroll.default_structure,
            salary_payout_mode: normalizeSalaryPayoutMode(
              payrollSub.salary_payout_mode || hr.salary_payout_mode || prev.payroll.salary_payout_mode
            ),
            tax: payrollSub.tax || prev.payroll.tax,
          },
        }));
      })
      .catch(() => {});
  }, [isAdmin, canManageRoles]);

  const openInlineTab = (tabId) => {
    setActiveTab(tabId);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tabId);
    window.history.replaceState({}, "", url);
    setTimeout(() => {
      document.getElementById("settings-inline-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const closeInlinePanel = () => {
    setActiveTab(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("tab");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  const onSave = async () => {
    try {
      setSaving(true);

      if (isAdmin && model.general.employee_code_prefix) {
        await API.patch("/organisation/me/hr-settings", {
          employee_prefix: model.general.employee_code_prefix.toUpperCase(),
        });
      }

      if (canManageRoles && activeTab === "payroll") {
        const salary_cycle = toApiPayrollCycle(model.payroll.payroll_cycle);
        const salary_payment_day = Number(model.payroll.salary_payment_day) || 1;
        const payroll_processing_day = Number(model.payroll.payroll_processing_day) || 25;
        const monthly_salary_divisor_days = Number(model.payroll.monthly_salary_divisor_days) || 26;

        await API.patch("/organisation/me/hr-settings", {
          salary_cycle,
          salary_payment_day,
          payroll_processing_day,
          monthly_salary_divisor_days,
          payroll: {
            salary_cycle,
            salary_payment_day,
            payroll_processing_day,
            monthly_salary_divisor_days,
            default_structure: model.payroll.default_structure,
            salary_payout_mode: normalizeSalaryPayoutMode(model.payroll.salary_payout_mode),
            tax: model.payroll.tax,
            total_working_days_mode: "fixed",
            payable_days_mode: "attendance_units",
            prorate_with_attendance: true,
            settings_completed: true,
            settings_saved_at: new Date().toISOString(),
          },
        });
        await refetchSetupProgress?.();
      }

      saveSettings(model);
      const msg =
        activeTab === "payroll"
          ? "Payroll settings saved — setup status updated"
          : "Settings saved successfully";
      setSaveFlash(msg);
      addToast(msg, "success", { title: "Updated successfully" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      addToast(typeof detail === "string" ? detail : "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-full min-w-0 p-4 sm:p-6 lg:p-8">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      {isStaff ? (
        <SettingsConfigurationHub role={role} onInlineSelect={openInlineTab} />
      ) : (
        <header className="mb-6 sm:mb-8">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">Settings</h1>
          <p className="mt-1 text-sm text-slate-600">Your account preferences and notifications.</p>
        </header>
      )}

      {activeTab ? (
        <div id="settings-inline-panel" className="settings-inline-panel">
          <SuccessCelebration
            open={Boolean(saveFlash)}
            title="Updated successfully"
            message={saveFlash}
            hint="Changes apply organisation-wide for your account settings."
            onDismiss={() => setSaveFlash("")}
            autoDismissMs={7000}
          />
          <div className="settings-inline-panel-head">
            <div>
              <h2 className="settings-inline-panel-title">
                {INLINE_TABS.find((t) => t.id === activeTab)?.label || "Settings"}
              </h2>
              <p className="settings-inline-panel-sub">Configure and save your changes below.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="settings-tab-pills">
                {INLINE_TABS.filter((t) => {
                  if (t.id === "access" && !canManageIam) return false;
                  if (t.id === "compliance" && !canManageRoles) return false;
                  if (t.id === "payroll" && role === "employee") return false;
                  return true;
                }).map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`settings-tab-pill ${activeTab === t.id ? "active" : ""}`}
                      onClick={() => openInlineTab(t.id)}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={closeInlinePanel}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                aria-label="Close panel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="settings-inline-panel-body">
            {activeTab === "general" ? (
              <Card title="General settings" description="Organisation defaults used across the system.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Organisation name">
                    <InputWithIcon
                      icon={Globe}
                      value={model.general.organisation_name}
                      onChange={(e) =>
                        setModel((p) => ({ ...p, general: { ...p.general, organisation_name: e.target.value } }))
                      }
                      placeholder="Organisation name"
                    />
                  </Field>
                  <Field label="Timezone">
                    <SelectWithIcon
                      icon={Timer}
                      value={model.general.timezone}
                      onChange={(e) => setModel((p) => ({ ...p, general: { ...p.general, timezone: e.target.value } }))}
                    >
                      <option value="Asia/Kolkata">Asia/Kolkata</option>
                      <option value="UTC">UTC</option>
                      <option value="Europe/London">Europe/London</option>
                      <option value="America/New_York">America/New_York</option>
                    </SelectWithIcon>
                  </Field>
                  <Field label="Currency">
                    <SelectWithIcon
                      icon={Wallet}
                      value={model.general.currency}
                      onChange={(e) => setModel((p) => ({ ...p, general: { ...p.general, currency: e.target.value } }))}
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </SelectWithIcon>
                  </Field>
                  <Field label="Date format">
                    <SelectWithIcon
                      icon={Calendar}
                      value={model.general.date_format}
                      onChange={(e) =>
                        setModel((p) => ({ ...p, general: { ...p.general, date_format: e.target.value } }))
                      }
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </SelectWithIcon>
                  </Field>
                  {isAdmin ? (
                    <Field label="Employee code prefix">
                      <InputWithIcon
                        icon={Globe}
                        value={model.general.employee_code_prefix || ""}
                        onChange={(e) => {
                          const raw = String(e.target.value || "")
                            .replace(/[^a-zA-Z]/g, "")
                            .toUpperCase()
                            .slice(0, 4);
                          setModel((p) => ({ ...p, general: { ...p.general, employee_code_prefix: raw } }));
                        }}
                        placeholder="EMP"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        Saved to your organisation. New employees use this prefix by default (e.g. HR0001).
                      </p>
                    </Field>
                  ) : null}
                </div>
              </Card>
            ) : null}

            {activeTab === "payroll" ? (
              <Card title="Payroll settings" description="Pay cycle, processing schedule, and tax defaults — saved to your organisation.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Default salary structure">
                    <InputWithIcon
                      icon={Wallet}
                      value={model.payroll.default_structure}
                      onChange={(e) =>
                        setModel((p) => ({ ...p, payroll: { ...p.payroll, default_structure: e.target.value } }))
                      }
                      placeholder="Structure name"
                    />
                  </Field>
                  <Field label="Payroll cycle" hint="How often salaries are processed">
                    <SelectWithIcon
                      icon={Timer}
                      value={model.payroll.payroll_cycle}
                      onChange={(e) =>
                        setModel((p) => ({ ...p, payroll: { ...p.payroll, payroll_cycle: e.target.value } }))
                      }
                    >
                      <option value="monthly">Monthly</option>
                      <option value="biweekly">Bi-Weekly</option>
                      <option value="weekly">Weekly</option>
                    </SelectWithIcon>
                  </Field>
                  <Field label="Salary payment day" hint="Day of month salary is paid (1–31)">
                    <InputWithIcon
                      icon={Calendar}
                      type="number"
                      min={1}
                      max={31}
                      value={model.payroll.salary_payment_day}
                      onChange={(e) =>
                        setModel((p) => ({ ...p, payroll: { ...p.payroll, salary_payment_day: e.target.value } }))
                      }
                    />
                  </Field>
                  <Field label="Payroll processing day" hint="Day payroll is processed each cycle (1–31)">
                    <InputWithIcon
                      icon={Timer}
                      type="number"
                      min={1}
                      max={31}
                      value={model.payroll.payroll_processing_day}
                      onChange={(e) =>
                        setModel((p) => ({ ...p, payroll: { ...p.payroll, payroll_processing_day: e.target.value } }))
                      }
                    />
                  </Field>
                  <Field label="Working days (salary divisor)" hint="Fixed days to divide monthly gross (e.g. 21 or 26)">
                    <InputWithIcon
                      icon={Calendar}
                      type="number"
                      min={1}
                      max={31}
                      value={model.payroll.monthly_salary_divisor_days}
                      onChange={(e) =>
                        setModel((p) => ({
                          ...p,
                          payroll: { ...p.payroll, monthly_salary_divisor_days: e.target.value },
                        }))
                      }
                    />
                  </Field>
                </div>

                <SalaryPayoutModeField
                  value={model.payroll.salary_payout_mode}
                  onChange={(mode) =>
                    setModel((p) => ({ ...p, payroll: { ...p.payroll, salary_payout_mode: mode } }))
                  }
                />

                <div className="mt-6">
                  <ExtraWorkConfigLink />
                </div>

                <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/40 p-4">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Tax configurations
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Toggle
                      label="TDS"
                      checked={model.payroll.tax.tds}
                      onChange={(v) =>
                        setModel((p) => ({ ...p, payroll: { ...p.payroll, tax: { ...p.payroll.tax, tds: v } } }))
                      }
                    />
                    <Toggle
                      label="PF"
                      checked={model.payroll.tax.pf}
                      onChange={(v) =>
                        setModel((p) => ({ ...p, payroll: { ...p.payroll, tax: { ...p.payroll.tax, pf: v } } }))
                      }
                    />
                    <Toggle
                      label="ESI"
                      checked={model.payroll.tax.esi}
                      onChange={(v) =>
                        setModel((p) => ({ ...p, payroll: { ...p.payroll, tax: { ...p.payroll.tax, esi: v } } }))
                      }
                    />
                  </div>
                </div>
              </Card>
            ) : null}

            {activeTab === "preferences" ? (
              <Card title="User preferences" description="Personal defaults for your account.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Theme mode">
                    <SelectWithIcon
                      icon={Palette}
                      value={model.preferences.theme_mode}
                      onChange={(e) =>
                        setModel((p) => ({ ...p, preferences: { ...p.preferences, theme_mode: e.target.value } }))
                      }
                    >
                      <option value="system">System</option>
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </SelectWithIcon>
                    <p className="mt-1 text-xs text-slate-500">Theme switching is future-ready.</p>
                  </Field>
                </div>

                <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/40 p-4">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Notifications</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Toggle
                      label="Email notifications"
                      icon={Bell}
                      checked={model.preferences.notifications.email}
                      onChange={(v) =>
                        setModel((p) => ({
                          ...p,
                          preferences: { ...p.preferences, notifications: { ...p.preferences.notifications, email: v } },
                        }))
                      }
                    />
                    <Toggle
                      label="In-app notifications"
                      icon={Bell}
                      checked={model.preferences.notifications.in_app}
                      onChange={(v) =>
                        setModel((p) => ({
                          ...p,
                          preferences: { ...p.preferences, notifications: { ...p.preferences.notifications, in_app: v } },
                        }))
                      }
                    />
                  </div>
                </div>
              </Card>
            ) : null}

            {activeTab === "compliance" ? (
              <Card
                title="Compliance & Statutory"
                description="Configure EPF, ESI, Professional Tax, LWF, and TDS."
              >
                <p className="text-sm text-slate-600">
                  Manage organization-level statutory components, government registrations, and compliance
                  dashboard. Settings sync with the payroll engine.
                </p>
                {canManageRoles ? (
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                      to="/settings/statutory"
                      className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                    >
                      <Shield className="h-4 w-4" />
                      Open Statutory Console
                    </Link>
                    <Link
                      to="/settings/statutory?tab=epf"
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      PF Settings
                    </Link>
                    <Link
                      to="/settings/statutory?tab=esi"
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      ESI Settings
                    </Link>
                    <Link
                      to="/settings/tds"
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      TDS Settings
                    </Link>
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-slate-500">Statutory configuration requires admin or HR access.</p>
                )}
              </Card>
            ) : null}

            {activeTab === "access" ? (
              <Card title="Security & Access" description="Users, roles, permissions, and audit.">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200/80">
                    <LockKeyhole className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                    <span>Your role: {formatRoleLabel(role)}</span>
                  </div>
                  {canManageIam ? (
                    <Link
                      to="/settings/security/users-roles"
                      className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                    >
                      <Shield className="h-4 w-4" />
                      Open Users &amp; Roles
                    </Link>
                  ) : (
                    <p className="text-xs text-slate-500">IAM is managed by organisation admin only.</p>
                  )}
                </div>
              </Card>
            ) : null}

            {["general", "payroll", "preferences"].includes(activeTab) ? (
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-sky-700 to-cyan-600 px-4 py-2.5 text-sm font-medium text-white shadow-md shadow-sky-900/15 transition duration-200 hover:scale-[1.02] hover:from-sky-800 hover:to-cyan-700 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40 active:scale-[0.98] disabled:opacity-50"
                >
                  <Cog className="h-4 w-4" aria-hidden />
                  {saving ? "Saving…" : "Save settings"}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : !isStaff ? (
        <div className="settings-inline-panel">
          <div className="settings-inline-panel-body">
            <button
              type="button"
              className="settings-tab-pill active mb-4"
              onClick={() => openInlineTab("preferences")}
            >
              <Palette className="h-3.5 w-3.5" />
              Open Preferences
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Card({ title, description, children }) {
  return (
    <section className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm shadow-slate-900/5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

function Toggle({ label, icon: Icon, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={[
        "flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm transition duration-200",
        checked ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white hover:bg-slate-50",
      ].join(" ")}
    >
      <span className="inline-flex items-center gap-2 text-slate-700">
        {Icon ? <Icon className="h-4 w-4 text-slate-500" aria-hidden /> : null}
        {label}
      </span>
      <span
        className={[
          "relative inline-flex h-6 w-10 items-center rounded-full p-0.5 transition duration-200",
          checked ? "bg-emerald-600" : "bg-slate-300",
        ].join(" ")}
        aria-hidden
      >
        <span
          className={[
            "h-5 w-5 rounded-full bg-white shadow-sm transition duration-200",
            checked ? "translate-x-4" : "translate-x-0",
          ].join(" ")}
        />
      </span>
    </button>
  );
}
